type AuthorizationHeaders = Record<string, string>;

export type AuthenticatedRequest<T> = (
  headers: AuthorizationHeaders,
) => Promise<T>;
export type RefreshAccessToken = () => Promise<string>;

const authorizationHeaders = (accessToken: string): AuthorizationHeaders =>
  accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

const responseStatus = (value: unknown): number | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  if ('status' in value && typeof value.status === 'number') {
    return value.status;
  }

  if ('response' in value) {
    return responseStatus(value.response);
  }

  return undefined;
};

export const withAuthRetry = async <T>(
  request: AuthenticatedRequest<T>,
  accessToken: string,
  refreshAccessToken?: RefreshAccessToken,
): Promise<T> => {
  const initial = await request(authorizationHeaders(accessToken)).then(
    value => ({ rejected: false as const, value }),
    reason => ({ rejected: true as const, value: reason }),
  );
  const returnInitial = (): T => {
    if (initial.rejected) {
      throw initial.value;
    }
    return initial.value;
  };

  if (responseStatus(initial.value) !== 401 || !refreshAccessToken) {
    return returnInitial();
  }

  let refreshedAccessToken: string;
  try {
    refreshedAccessToken = await refreshAccessToken();
  } catch {
    return returnInitial();
  }

  return request(authorizationHeaders(refreshedAccessToken));
};
